import { generateUniqueIdWithIdPrefix } from '../../utils/unique-id';
class LayoutTabs {
  private layoutTabs: HTMLElement;
  private tabsList: HTMLElement;
  private panelContainer: HTMLElement;
  private tabs: NodeListOf<HTMLElement>;
  private panels: NodeListOf<HTMLElement>;
  private newTabs: HTMLButtonElement[] = [];
  private callouts: NodeListOf<HTMLElement>;

  constructor(layoutTabs: HTMLElement) {
    this.layoutTabs = layoutTabs;
    this.tabsList = this.layoutTabs.querySelector('.layout-tabs__tab-list') as HTMLElement;
    this.tabs = this.layoutTabs.querySelectorAll('.layout-tabs__tab-list > *');
    this.panelContainer = this.layoutTabs.querySelector('.layout-tabs__panels') as HTMLElement;
    this.panels = this.layoutTabs.querySelectorAll('.layout-tabs__panels > *');
    this.callouts = this.layoutTabs.querySelectorAll('.layout-tabs__callout > *');
    this.init();
  }

  init(): void {
    if (document.documentElement.classList.contains('sfPageEditorWrp')) {
      return;
    }
    this.loadStyles();
    this.setupTabs();
  }

  private loadStyles(): void {
    const style = document.createElement('link');
    style.rel = 'stylesheet';
    style.href = '/ResourcePackages/Talon/assets/dist/css/layout-tabs-styles.min.css';
    document.head.appendChild(style);
  }

  private setupTabs(): void {
    this.tabs.forEach((tab, index) => {
      const createdTab = this.createTab(tab, index);
      const createdPanel = this.createPanel(createdTab, index);
      this.tabsList.insertAdjacentElement('beforeend', createdTab);
      this.panelContainer.insertAdjacentElement('beforeend', createdPanel);
      this.newTabs.push(createdTab);
      tab.parentElement?.removeChild(tab);
    });
  }

  private createTab(tab: HTMLElement, index: number): HTMLButtonElement {
    const newTab = document.createElement('button');
    const tabText = tab.textContent.trim();
    const tabId = generateUniqueIdWithIdPrefix();

    newTab.setAttribute('id', tabId);
    newTab.classList.add('layout-tabs__tab');
    newTab.classList.add('subhead');
    newTab.setAttribute('aria-controls', `panel-${tabId}`);
    newTab.setAttribute('role', 'tab');
    newTab.textContent = tabText;

    if (index === 0) {
      newTab.classList.add('active');
      newTab.setAttribute('aria-selected', 'true');
      const relatedCallout = this.callouts[index] as HTMLElement;
      if (relatedCallout) {
        relatedCallout.classList.add('active');
      }
    } else {
      newTab.setAttribute('tabindex', '-1');
      newTab.setAttribute('aria-selected', 'false');
    }
    newTab.addEventListener('click', (e) => {
      e.preventDefault();
      this.activateTab(newTab);
    });

    newTab.addEventListener('keydown', (e) => {
      this.handleTabKeyDown(e);
    });
    return newTab;
  }

  private createPanel(tab: HTMLButtonElement, index: number): HTMLDivElement {
    const panel = this.panels[index];
    const panelClone = panel.cloneNode(true) as HTMLElement;
    const newPanel = document.createElement('div');
    const tabId = tab.getAttribute('id') as string;
    const panelId = `panel-${tabId}`;
    newPanel.setAttribute('id', panelId);
    newPanel.classList.add('layout-tabs__panel');
    newPanel.setAttribute('aria-labelledby', tabId);
    newPanel.setAttribute('role', 'tabpanel');
    newPanel.appendChild(panelClone);
    panel.parentElement?.removeChild(panel);
    if (index === 0) {
      newPanel.classList.add('active');
    }
    return newPanel;
  }

  private activateTab(tab: HTMLButtonElement): void {
    const relatedPanel = document.getElementById(tab.getAttribute('aria-controls') as string) as HTMLElement;
    const relatedCallout = this.callouts[this.newTabs.indexOf(tab)] as HTMLElement;
    this.deactivateAllTabs();
    relatedPanel.classList.add('active');
    relatedPanel.setAttribute('tabindex', '0');
    relatedPanel.focus();
    if (relatedCallout) {
      relatedCallout.classList.add('active');
    }
    tab.classList.add('active');
    tab.setAttribute('aria-selected', 'true');
    tab.removeAttribute('tabindex');
  }

  private deactivateTab(tab: HTMLButtonElement): void {
    const relatedPanel = document.getElementById(tab.getAttribute('aria-controls') as string) as HTMLElement;
    const relatedCallout = this.callouts[this.newTabs.indexOf(tab)] as HTMLElement;

    relatedPanel.classList.remove('active');
    if (relatedCallout) {
      relatedCallout.classList.remove('active');
    }
    tab.classList.remove('active');
    tab.setAttribute('aria-selected', 'false');
    tab.setAttribute('tabindex', '-1');
  }

  private deactivateAllTabs(): void {
    this.newTabs.forEach((tab) => {
      if (tab.classList.contains('active')) {
        this.deactivateTab(tab);
        const relatedPanel = document.getElementById(tab.getAttribute('aria-controls') as string) as HTMLElement;
        relatedPanel.removeAttribute('tabindex');
      }
    });
  }

  private handleTabKeyDown(e: KeyboardEvent): void {
    const currentTab = e.target as HTMLButtonElement;
    const currentIndex = this.newTabs.indexOf(currentTab);

    if (currentIndex === -1) {
      return;
    }

    let targetIndex: number | null = null;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        if (currentIndex > 0) {
          targetIndex = currentIndex - 1;
        }
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (currentIndex < this.newTabs.length - 1) {
          targetIndex = currentIndex + 1;
        }
        break;
      case 'Home':
        e.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        targetIndex = this.newTabs.length - 1;
        break;
      default:
        return;
    }

    if (targetIndex !== null && targetIndex !== currentIndex) {
      this.deactivateAllTabs();

      // Activate the target tab
      const targetTab = this.newTabs[targetIndex];
      this.activateTab(targetTab);
      targetTab.focus();
    }
  }
}

document.querySelectorAll('[layout-tabs]').forEach((layoutTabs) => {
  new LayoutTabs(layoutTabs as HTMLElement);
});
